import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles, inputStyle, labelStyle, fieldStyle } from "../shared/styles";
import { SimpleModal } from "../shared/SimpleModal";

export function AssetTypesSettings() {
  const [categories, setCategories] = useState([]);
  const [allTypes, setAllTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [modalCategoryId, setModalCategoryId] = useState(null);
  const [typeName, setTypeName] = useState("");
  const [typeActive, setTypeActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [collapsed, setCollapsed] = useState({});

  const loadAll = () => {
    Promise.all([
      axios.get(`${API}/asset-categories/`),
      axios.get(`${API}/asset-types/`),
    ])
      .then(([catRes, typeRes]) => {
        setCategories(catRes.data);
        setAllTypes(typeRes.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };
  useEffect(() => {
    loadAll();
  }, []);

  const openAdd = (categoryId) => {
    setEditingType(null);
    setModalCategoryId(categoryId);
    setTypeName("");
    setTypeActive(true);
    setError("");
    setShowModal(true);
  };
  const openEdit = (t) => {
    setEditingType(t);
    setModalCategoryId(t.category);
    setTypeName(t.name);
    setTypeActive(t.is_active);
    setError("");
    setShowModal(true);
  };
  const handleSave = async () => {
    if (!typeName.trim()) {
      setError("Name is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (editingType) {
        await axios.patch(`${API}/asset-types/${editingType.id}/`, {
          name: typeName,
          is_active: typeActive,
          category: modalCategoryId,
        });
      } else {
        await axios.post(`${API}/asset-types/`, {
          name: typeName,
          is_active: typeActive,
          category: modalCategoryId,
        });
      }
      setShowModal(false);
      loadAll();
    } catch (e) {
      setError("Something went wrong.");
    }
    setSaving(false);
  };
  const toggleCollapse = (catId) =>
    setCollapsed((prev) => ({ ...prev, [catId]: !prev[catId] }));

  return (
    <div>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Asset Types</div>
      </div>
      <div style={styles.content}>
        <div style={styles.card}>
          <div style={{ ...styles.tableHeader, gridTemplateColumns: "2fr 80px" }}>
            <div>Asset Type</div>
            <div></div>
          </div>
          {loading ? (
            <div style={styles.loading}>Loading...</div>
          ) : (
            categories.map((cat) => {
              const types = allTypes.filter((t) => t.category === cat.id);
              const isCollapsed = collapsed[cat.id];
              return (
                <React.Fragment key={cat.id}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 20px",
                      backgroundColor: colors.darkGrey,
                      cursor: "pointer",
                      borderBottom: `1px solid rgba(255,255,255,0.06)`,
                    }}
                    onClick={() => toggleCollapse(cat.id)}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <span style={{ color: colors.white, fontWeight: "600", fontSize: "13px" }}>
                        {cat.name}
                      </span>
                      <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "12px" }}>
                        {types.length} type{types.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); openAdd(cat.id); }}
                        style={{
                          backgroundColor: colors.red,
                          color: colors.white,
                          border: "none",
                          padding: "4px 12px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor: "pointer",
                          minHeight: "auto",
                        }}
                      >
                        + Add Type
                      </button>
                      <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "12px" }}>
                        {isCollapsed ? "▶" : "▼"}
                      </span>
                    </div>
                  </div>
                  {!isCollapsed && (
                    types.length === 0 ? (
                      <div style={{ padding: "12px 20px", fontSize: "13px", color: "#94a3b8", fontStyle: "italic", borderBottom: `1px solid ${colors.border}` }}>
                        No types yet — click + Add Type to get started.
                      </div>
                    ) : (
                      types.map((t, idx) => (
                        <div
                          key={t.id}
                          style={{
                            ...styles.tableRow(false),
                            gridTemplateColumns: "2fr 80px",
                            borderBottom: `1px solid ${colors.border}`,
                          }}
                        >
                          <div style={{ fontWeight: "500", fontSize: "14px", color: colors.text }}>
                            {t.name}
                          </div>
                          <div>
                            <button
                              onClick={() => openEdit(t)}
                              style={{
                                backgroundColor: colors.blue,
                                color: colors.white,
                                border: "none",
                                padding: "4px 14px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: "600",
                                cursor: "pointer",
                                minHeight: "auto",
                              }}
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                      ))
                    )
                  )}
                </React.Fragment>
              );
            })
          )}
          <div style={styles.count}>
            {allTypes.length} type{allTypes.length !== 1 ? "s" : ""} across {categories.length} categor{categories.length !== 1 ? "ies" : "y"}
          </div>
        </div>
      </div>
      {showModal && (
        <SimpleModal
          title={
            editingType
              ? `Edit: ${editingType.name}`
              : `New Type — ${categories.find((c) => c.id === modalCategoryId)?.name}`
          }
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          saving={saving}
          error={error}
        >
          <div style={fieldStyle}>
            <label style={labelStyle}>Asset Type Name *</label>
            <input
              style={inputStyle}
              value={typeName}
              onChange={(e) => setTypeName(e.target.value)}
              placeholder="Enter asset type name"
              autoFocus
            />
          </div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={typeActive}
              onChange={(e) => setTypeActive(e.target.checked)}
            />
            Active
          </label>
        </SimpleModal>
      )}
    </div>
  );
}
